import * as XLSX from 'xlsx';
import { ExcelTemplateConfig } from 'src/utils/excel/excelGridConfig';

const getDateString = (): string => {
	const today = new Date();
	return `${today.getFullYear()}${String(today.getMonth() + 1).padStart(2, '0')}${String(today.getDate()).padStart(2, '0')}`;
};

export const buildHeaderKeyMap = (headerNames: Record<string, string>): Record<string, string> => {
	return Object.entries(headerNames).reduce((acc, [key, value]) => {
		if (value) {
			acc[value] = key;
		}
		return acc;
	}, {} as Record<string, string>);
};

export const parseExcelFile = (file: File): Promise<Record<string, unknown>[]> => {
	return new Promise((resolve, reject) => {
		const reader = new FileReader();
		reader.onload = (e) => {
			try {
				const data = e.target?.result;
				const workbook = XLSX.read(data, { type: 'array' });
				const sheetName = workbook.SheetNames[0];
				const worksheet = workbook.Sheets[sheetName];
				const json = XLSX.utils.sheet_to_json<Record<string, unknown>>(worksheet);
				resolve(json);
			} catch (error) {
				reject(error);
			}
		};
		reader.onerror = () => reject(new Error('파일 읽기 실패'));
		reader.readAsArrayBuffer(file);
	});
};

export const mapRowFromExcel = <T extends object>(
	row: Record<string, unknown>,
	headerKeyMap: Record<string, string>,
	defaultRow: T,
	valueTransformers?: Partial<Record<string, (value: unknown) => unknown>>
): T => {
	const newRow: Record<string, unknown> = { ...(defaultRow as Record<string, unknown>) };

	for (const excelHeader in row) {
		const dtoKey = headerKeyMap[excelHeader];
		if (!dtoKey) continue;

		let value = row[excelHeader];
		const transformer = valueTransformers?.[dtoKey];
		if (transformer) {
			value = transformer(value);
		}
		newRow[dtoKey] = value;
	}

	return newRow as T;
};

export const validateRequiredHeaders = (
	excelHeaders: string[],
	requiredHeaders: string[]
): string[] => {
	return requiredHeaders.filter((header) => !excelHeaders.includes(header));
};

export const getNextValueIndexes = (
	existingList: { valueIndex?: number | string }[],
	count: number
): number[] => {
	const base = Math.max(0, ...existingList.map((item) => Number(item.valueIndex) || 0));
	return Array.from({ length: count }, (_, index) => base + index + 1);
};

export const downloadExcelTemplate = (config: ExcelTemplateConfig): void => {
	const {
		columnOrder,
		headerNames,
		colWidths,
		sheetName = '템플릿',
		fileNamePrefix = '업로드_템플릿',
		sampleData = [],
	} = config;

	const headers = columnOrder.map((field) => headerNames[field]);
	const wb = XLSX.utils.book_new();
	const data = sampleData.length > 0 ? [headers, ...sampleData] : [headers];
	const ws = XLSX.utils.aoa_to_sheet(data);

	if (colWidths && colWidths.length > 0) {
		ws['!cols'] = colWidths.map((width) => ({ wch: width }));
	}

	XLSX.utils.book_append_sheet(wb, ws, sheetName);
	XLSX.writeFile(wb, `${fileNamePrefix}_${getDateString()}.xlsx`);
};
